import React, { useState, useEffect, useCallback } from 'react';
import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, QuestionMarkCircleIcon } from '@heroicons/react/20/solid'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { connect } from 'react-redux';
import Select from 'react-select';
import { Actions as SALARYCALCULATORACTIONS } from '../store/actions/salaryCalculatorActions';
import { Actions as CREATEACCOUNTACTIONS } from '../store/actions/createAccountActions';
import ReactTooltip from 'react-tooltip';
import _ from 'lodash';
import './salaryCalc.css';
import { differenceInCalendarYears, getYear } from 'date-fns';

const {colaIncreases,minimumWage} = SALARYCALCULATORACTIONS

const frequencies = [
    { value: 'Hour', label: 'Hourly' },
    { value: 'Day', label: 'Daily' },
    { value: 'Week', label: 'Weekly' },
    { value: 'Month', label: 'Monthly' },
    { value: 'Year', label: 'Yearly' },
]

const payPeriods = [
    { value: 'Week', label: 'View Weekly Pay' },
    { value: 'Month', label: 'View Monthly Pay' },
    { value: 'Year', label: 'View Annualised Pay' },
]

const years = [...Array(differenceInCalendarYears(new Date(), new Date(2018, 1, 1)) + 1)].map((item, index) => ({ value: `${2018 + index}`, label: `${2018 + index}` }))

// if COLA increases has next year, add it to the list
if (colaIncreases?.[parseInt(getYear(new Date()))+1]) years.push({ value: `${parseInt(getYear(new Date()))+1}`, label: `${parseInt(getYear(new Date()))+1}` })

const childrenOptions = [
    { value: "no-children", label: 'No Children' },
    { value: "one-child", label: '1 Child' },
    { value: "two-or-more-children", label: '2+ Children' },
]

const parentChildrenOptions = [
    { value: "one-child", label: '1 Child' },
    { value: "two-or-more-children", label: '2+ Children' },
]

const comparisonYears = [2026, 2027, 2028].map(year => ({ value: year, label: String(year) }))

const earningColors = [
    "#EA611C",
    "#B68D40",
    "#F4EBD0",
    "#122620",
    "#FAB300",
    "#D6AD60",
    "#22508A",
    "#FFD78C",
];

const deductionColors = [
    "#7F80EB",
    "#FB6090",
    "#DF362D",
    "#E57F84",
    "#2F5061",
    "#4297A0",
    "#B4F8C8",
    "#821D30",
];

const netDefaultColor = "#66EE66";

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const SalaryCalculator = ({
    options,
    // backgroundColor, // background panel color: default gray-100
    // primaryColor, // button colour: default orange-400
    // innerChartColours = [], // chart earning colors
    // outerChartColours = [], // chart deduction colors
    // chartNetColor = [], // chart net color
    // footerTextColor, // powered by Buddy text
    colaDifference,
    calculateSalary,
    earnings,
    deductions,
    net,
    requestReport,
    error,
    saving,
    downloadLink,
    clearReportLink,
    profile
}) => {

    const {
        primaryColor,
        backgroundColor,
        innerChartColours,
        outerChartColours,
        netColor,
        poweredByTextColor,
        footerTextColor,
    } = (options || {})

    const publishingOptions = [
        { title: 'Single', value: 505, description: 'Used for single individuals, or couples (married/in civil-union) without children where both work and rate is benefical', current: true },
        { title: 'Married', value: 507, description: 'Used by couples, married or in civil-union, when only one works or rate is more beneficial than single or parent rates when applicable. Also applicable for single-parents that support a household.', current: false },
        { title: 'Parent', value: 506, description: 'Applies when a child is not over 18 (or 23 years old, when studying full time) and not gainfully occupied. When gainfully occupied, they did not earn more than €3,400. Only use if more preferential than married rate.', current: false },
        { title: 'Highly Qualified Person', value: 510, description: 'Special 15% flat tax-rate, for specific roles with income over €86,938. Requires application.', current: false },
        { title: 'Non Resident Tax', value: 302984, description: 'For persons who do not reside in Malta (less than 180 days)', current: false },
        { title: 'Overseas Employment', value: 509, description: 'Tax applicable when work done overseas is taxed in Malta', current: false },
        { title: 'Part-time (Qualified)', value: 511, description: 'A 15% shall apply for the first €10,000 income from your part-time role. Applies only if you are already employed, a pensioneer or a part-time student.', current: false },
        { title: 'Qualified Sports', value: 503, description: 'Athletes and Coaches can opt for a flat 7.5% tax rate.', current: false },
        { title: 'Other', value: 521, description: 'Custom tax rate - usually used to accomodate blended rates for part-time employees.', current: false },
    ]

    const [salary, setSalary] = useState(20000);
    const [overtime, setOvertime] = useState(0);
    const [carAllowance, setCarAllowance] = useState(0);
    const [bonus, setBonus] = useState(0);
    const [isApprentice, setIsApprentice] = useState(false);
    const [age, setAge] = useState(false);
    const [oldAge, setOldAge] = useState(false);
    const [frequency, setFrequency] = useState("Year");
    const [payPeriod, setPayPeriod] = useState("Month");
    const [selected, setSelected] = useState(publishingOptions[0])
    const [customTax, setCustomTax] = useState(0);
    const [showDownloadForm, setShowDownloadForm] = useState(false);
    const [year, setYear] = useState(getYear(new Date()))
    const [cola, setCola] = useState(false)
    const [children, setChildren] = useState(parentChildrenOptions[0]?.value)
    const [comparisonYear, setComparisonYear] = useState(comparisonYears[0]?.value)

    const frequencyLabel = frequencies.find(f => f.value == frequency).label

    let earningsTotal = 0
    let deductionsTotal = 0
    let adjustedCarAllowance = carAllowance
    let adjustedBonus = bonus
    let adjustedOvertime = overtime

    // switch (frequency) {
    //     case 'Year':
    //         adjustedCarAllowance = carAllowance / 12
    //         adjustedBonus = bonus / 12
    //         adjustedOvertime = bonus / 12
    //         break;
    // }

    const [data, setData] = useState({
        noOfEmployees: "1-5",
        name: "",
        surname: "",
        phone: "",
        organisation: "",
        email: "",
    });

    useEffect(() => {
        if (profile) {
            setData({
                ...data,
                name: profile.givenName || profile.name,
                surname: profile.familyName || profile.surname,
                email: profile.email,
            })
        }
    }, [profile]);


    const handleChange = (event) => {
        setData({
            ...data,
            [event.target.name]: event.target.value
        })
    }

    const submitForm = () => {
        requestReport({
            payslipInput: saveData,
            lead: data,
            branding: options,
            sendTo: options && options.sendTo,
        })
        // TODO send email
    }

    // POST Save Data
    const saveData = {
        year,
        amount: parseFloat(salary),
        frequency: frequency,
        payPeriod,
        overtimeHours: parseFloat(adjustedOvertime),
        CarCashAllowance: parseFloat(adjustedCarAllowance),
        performanceBonus: parseFloat(adjustedBonus),
        isApprentice: isApprentice,
        age: age ? 17 : 18,
        dateOfBirth: oldAge ? "1961-01-01" : "1990-01-01",
        cola,
        taxStatus: selected.value,
        customTaxRate: `${customTax}%`,
        subType: children,
        comparisonYear: comparisonYear
    }

    Object.keys(earnings).map(key => {
        if (earnings[key].value != 0)
            earningsTotal += earnings[key].value
    })

    Object.keys(deductions).map(key => {
        if ((deductions[key].payer == "Employee" || deductions[key].payer == "Both") && deductions[key].value != 0)
            deductionsTotal += deductions[key].value
    })


    const debounceCalculateSalary = useCallback(_.debounce((saveData) => {
        calculateSalary(saveData)
        if (downloadLink) {
            clearReportLink()
        }
    }, 200, { leading: true, trailing: true }), []);
    useEffect(() => {

        debounceCalculateSalary(saveData);
        // calculateSalary(saveData)

    }, [
        year,
        salary,
        overtime,
        carAllowance,
        bonus,
        isApprentice,
        age,
        oldAge,
        cola,
        frequency,
        selected,
        customTax,
        payPeriod,
        children,
        comparisonYear,
    ])

    Highcharts.setOptions({
        lang: {
            thousandsSep: ','
        }
    })

    const chartOptions = {
        chart: {
            type: 'pie',
            backgroundColor: 'transparent',
            height: 250,
            credits: false,
        },
        credits: false,
        title: {
            text: ""
        },
        tooltip: {
            pointFormat: "€{point.y:,.2f}"
        },
        series: [{
            size: '95%',
            innerSize: '65%',
            dataLabels: {
                distance: 5,
            },
            data: [...Object.keys(deductions).map(key => (
                {
                    color: outerChartColours ? outerChartColours[key] : deductionColors[key],
                    name: (deductions[key].payer == "Employee" || deductions[key].payer == "Both") && deductions[key].value != 0 ? deductions[key].name : null,
                    y: (deductions[key].payer == "Employee" || deductions[key].payer == "Both") && deductions[key].value != 0 ? deductions[key].value : null,
                }
            )),
            {
                color: netColor || netDefaultColor,
                name: "Net Salary",
                y: net != 0 ? net : null
            }
            ]
        },
        {
            size: '60.5%',
            dataLabels: {
                distance: 40,
            },
            data: Object.keys(earnings).map(key => (
                {
                    color: innerChartColours ? innerChartColours[key] : earningColors[key],
                    name: (earnings[key].value != 0) ? earnings[key].name : null,
                    y: (earnings[key].value != 0) ? earnings[key].value : null,
                }
            ))
        }],
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        calculateSalary(saveData)
    }

    const handleClick = () => {
        setShowDownloadForm(!showDownloadForm)
    }

    return (
        <div className='buddy-hr-widget col-span-12'>
            <div className="col-span-12 grid sm:grid-cols-12">
                <div className="sm:text-center mr-1 ml-0 md:mx-4 sm:col-span-5 lg:text-left lg:flex items-start mb-4">
                    <div className="md:mt-0 md:col-span-2">
                        <div className={`px-4 py-5 space-y-4 sm:p-3 rounded-md${backgroundColor ? "" : " bg-gray-100"}`} style={backgroundColor && { backgroundColor: backgroundColor }}>
                            <form onSubmit={handleSubmit} className='buddy-hr-widget-input'>
                                <div className="shadow rounded-md">
                                    <div className="rounded-md px-4 py-5 bg-white sm:px-3">
                                        <div className="grid grid-cols-6 gap-4 items-end">
                                            <div className="col-span-6">
                                                <div className="relative">
                                                    <label htmlFor="year" className="ml-1 block text-sm font-medium text-gray-700">
                                                        Year
                                                    </label>
                                                </div>
                                                <div className="relative rounded-md shadow-sm w-full">
                                                    <Select
                                                        components={{
                                                            IndicatorSeparator: () => null
                                                        }}
                                                        className="w-full z-20 text-sm"
                                                        defaultValue={years.find(y => y.value == getYear(new Date()))}
                                                        id="year"
                                                        name="year"
                                                        selected={years.find(y => y.value == year)}
                                                        onChange={(year) => {
                                                            setYear(year.value)
                                                            if (year.value < 2022) setCola(false)
                                                        }}
                                                        styles={{
                                                            menu: (base) => ({
                                                                ...base,
                                                                  width: "max-content",
                                                                minWidth: "100%"
                                                            }),
                                                        }}
                                                        options={years}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-5">
                                    <div className="shadow rounded-md">
                                        <div className="rounded-md px-4 py-5 bg-white sm:px-3">
                                            <div className="grid grid-cols-6 gap-4 items-end">
                                                <div className="col-span-6 sm:col-span-3">
                                                    <div className="relative year-selector">
                                                        <label htmlFor="salary" className="ml-1 block text-sm font-medium text-gray-700">
                                                            Salary
                                                        </label>


                                                        <div className="absolute inset-y-0 right-1 flex items-center text-xs font-light" style={{ paddingRight: "0.05rem", width: "6.15rem" }}>
                                                            <Select
                                                                styles={{ control: base => ({ ...base, border: 0, boxShadow: "none", backgroundColor: "none" }) }}
                                                                components={{
                                                                    IndicatorSeparator: () => null
                                                                }}
                                                                className="w-full text-sm"
                                                                defaultValue={frequencies[4]}
                                                                name="frequency"
                                                                selected={frequencies.find(f => f.value == frequency)}
                                                                onChange={(val) => setFrequency(val.value)}
                                                                options={frequencies}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="relative rounded-md shadow-sm w-full">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <span className="text-gray-500 sm:text-sm">€</span>
                                                        </div>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            id="salary"
                                                            name="salary"
                                                            defaultValue="20000"
                                                            value={salary}
                                                            onChange={(e) => setSalary(e.target.value)}
                                                            className="w-full pl-7 pr-2 sm:text-sm border border-gray-300 rounded-md h-10 focus:z-10 focus:ring-orange-400 focus:border-orange-400"
                                                            placeholder="0.00"
                                                        />
                                                        {
                                                            // check if salary is below minimum wage
                                                            salary < minimumWage[year].age['18'][frequency] &&
                                                            <div className="absolute right-0 pr-3 flex items-center cursor-pointer" onClick={() => {
                                                                setSalary(minimumWage[year].age['18'][frequency])
                                                            }}>
                                                                <span className="text-red-500 sm:text-xs">Minimum Wage {minimumWage[year].age['18'][frequency].toFixed(2)}</span>
                                                            </div>
                                                        }
                                                    </div>
                                                </div>
                                                <div className="col-span-6 sm:col-span-3">
                                                    <label htmlFor="overtime" className="block text-sm font-medium text-gray-700">
                                                        Overtime <span className="text-xs font-light">({frequencyLabel})</span>
                                                    </label>
                                                    <div className="mt-1 relative rounded-md shadow-sm">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="overtime"
                                                            id="overtime"
                                                            onChange={(e) => setOvertime(e.target.value)}
                                                            className="block w-full pl-3 pr-14 sm:text-sm border border-gray-300 rounded-md h-10 focus:z-10 focus:ring-orange-400 focus:border-orange-400"
                                                            placeholder="0"
                                                        />
                                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                            <span className="text-gray-500 sm:text-sm">Hours</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-span-6 sm:col-span-3">
                                                    <label htmlFor="allowance" className="block text-sm font-medium text-gray-700">
                                                        Car allowance <span className="text-xs font-light">({frequencyLabel})</span>
                                                    </label>
                                                    <div className="mt-1 relative rounded-md shadow-sm">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <span className="text-gray-500 sm:text-sm">€</span>
                                                        </div>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="allowance"
                                                            id="allowance"
                                                            onChange={(e) => setCarAllowance(e.target.value)}
                                                            className="block w-full pl-7 sm:text-sm border border-gray-300 rounded-md h-10 focus:z-10 focus:ring-orange-400 focus:border-orange-400"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-6 sm:col-span-3">
                                                    <label htmlFor="bonus" className="block text-sm font-medium text-gray-700">
                                                        Performance Bonus <span className="text-xs font-light">({frequencyLabel})</span>
                                                    </label>
                                                    <div className="mt-1 relative rounded-md shadow-sm">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <span className="text-gray-500 sm:text-sm">€</span>
                                                        </div>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="bonus"
                                                            id="bonus"
                                                            onChange={(e) => setBonus(e.target.value)}
                                                            className="block w-full pl-7 sm:text-sm border border-gray-300 rounded-md h-10  focus:z-10 focus:ring-orange-400 focus:border-orange-400"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                {console.log('cola',colaIncreases?.[parseInt(year)+1],year,parseInt(year)+1) && null}
                                                {colaIncreases?.[parseInt(year)+1] &&
                                                    <div className="col-span-12 sm:col-span-6">
                                                        <div className="flex items-start">
                                                            <div className="flex items-center h-5">
                                                                <input
                                                                    id="cola"
                                                                    name="cola"
                                                                    type="checkbox"
                                                                    checked={cola}
                                                                    onChange={(e) => {
                                                                        setCola(e.target.checked)
                                                                    }}
                                                                    className={`focus:z-10 focus:ring-transparent h-4 w-4 border-gray-300 rounded${primaryColor ? "" : " text-orange-500"}`}
                                                                />
                                                            </div>
                                                            <div className="ml-3 text-sm">
                                                                <label htmlFor="cola" className="font-medium text-gray-700">
                                                                    {year >= 2022 ? `Calculate ${parseInt(year) + 1} Salary including COLA` : `Add Cost of Living Adjustment`}
                                                                </label>
                                                                <p className="text-gray-500">The Adjustment for {parseInt(year) + 1} is{colaIncreases?.[parseInt(year)+1]?.estimated ? ` estimated to be`: ``} €{colaIncreases?.[parseInt(year)+1]?.Week}/Week</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-5 md:mt-0 md:col-span-2">
                                    <div className="shadow rounded-md">
                                        <div className="bg-white rounded-md space-y-3 p-5 sm:px-3">

                                            {/* Start Tax Rate Selection */}
                                            <div className="col-span-6 sm:col-span-3">
                                                <div>
                                                    <legend className="ml-1 block text-sm font-medium text-gray-700">Tax Options</legend>
                                                </div>
                                                <Listbox value={selected} onChange={setSelected}>
                                                    {({ open }) => (
                                                        <>
                                                            <div className="relative">
                                                                <div className="inline-flex shadow-sm rounded-md divide-x divide-gray-600 border border-solid w-full">
                                                                    <div className="relative z-0 inline-flex shadow-sm rounded-md w-full">
                                                                        <div className="relative inline-flex items-center py-2 pl-3 pr-4 border border-transparent rounded-l-md shadow-sm text-gray-700 flex-grow">
                                                                            <p className="ml-1 text-sm font-medium">{selected.title}</p>
                                                                        </div>
                                                                        <Listbox.Button className={`relative inline-flex items-center p-2 rounded-l-none rounded-r-md text-sm font-medium text-gray-700 focus:z-10 focus:ring-offset-2 focus:ring-offset-gray-50${primaryColor ? "" : ` bg-orange-400 hover:bg-orange-500`}`} style={primaryColor && { backgroundColor: primaryColor }}>
                                                                            <ChevronDownIcon className="h-5 w-5 text-white" aria-hidden="true" />
                                                                        </Listbox.Button>
                                                                    </div>
                                                                </div>
                                                                <Transition
                                                                    show={open}
                                                                    leave="transition ease-in duration-100"
                                                                    leaveFrom="opacity-100"
                                                                    leaveTo="opacity-0"
                                                                >
                                                                    <Listbox.Options
                                                                        static
                                                                        className="origin-top-left absolute z-50 left-0 mt-2 w-72 rounded-md shadow-lg bg-white divide-y divide-gray-200 ring-1 ring-black ring-opacity-5 focus:outline-none max-h-72 overflow-y-scroll"
                                                                    >
                                                                        {publishingOptions.map((option) => (
                                                                            <Listbox.Option
                                                                                key={option.title}
                                                                                className={({ active }) =>
                                                                                    classNames(
                                                                                        active ? 'text-gray-700' : 'text-gray-900',
                                                                                        'cursor-default select-none relative p-4 text-sm'
                                                                                    )
                                                                                }
                                                                                value={option}
                                                                            >
                                                                                {({ selected, active }) => (
                                                                                    <div className="flex flex-col">
                                                                                        <div className="flex justify-between">
                                                                                            <p className={selected ? 'font-semibold' : 'font-normal'}>{option.title}</p>
                                                                                            {selected ? (
                                                                                                <span className={active ? 'text-gray-700' : 'text-orange-400'}>
                                                                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                                                                </span>
                                                                                            ) : null}
                                                                                        </div>
                                                                                        <p className={classNames(active ? 'text-orange-300' : 'text-gray-500', 'mt-2')}>
                                                                                            {option.description}
                                                                                        </p>
                                                                                    </div>
                                                                                )}
                                                                            </Listbox.Option>
                                                                        ))}
                                                                    </Listbox.Options>
                                                                </Transition>
                                                            </div>
                                                        </>
                                                    )}
                                                </Listbox>
                                            </div>
                                            {selected && selected.title == "Other" &&
                                                <div className="col-span-6 sm:col-span-3">
                                                    <label htmlFor="rate" className="block text-sm font-medium text-gray-700">
                                                        Custom rate
                                                    </label>
                                                    <div className="mt-1 relative rounded-md shadow-sm w-3/4">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            name="rate"
                                                            id="rate"
                                                            onChange={(e) => setCustomTax(e.target.value)}
                                                            className="block w-full pl-3 sm:text-sm border-gray-300 rounded-md h-10 border focus:z-10 focus:ring-orange-400 focus:border-orange-400"

                                                            placeholder="0"
                                                        />
                                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                            <span className="text-gray-500 sm:text-sm">%</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            {(selected && (selected.title == 'Married' || selected.title == 'Parent')) &&
                                                <div className="col-span-6 sm:col-span-3">
                                                    <label htmlFor="children" className="block text-sm font-medium text-gray-700">
                                                        Number of Children
                                                    </label>
                                                    <div className="mt-1 relative rounded-md shadow-sm w-full">
                                                        <Select
                                                            components={{
                                                                IndicatorSeparator: () => null
                                                            }}
                                                            className="w-full text-sm"
                                                            id="children"
                                                            name="children"
                                                            value={(selected.title == 'Married' ? childrenOptions : parentChildrenOptions).find(c => c.value === children) || null}
                                                            onChange={(option) => setChildren(option.value)}
                                                            styles={{
                                                                menu: (base) => ({
                                                                    ...base,
                                                                    width: "max-content",
                                                                    minWidth: "100%"
                                                                }),
                                                            }}
                                                            options={selected.title == 'Married' ? childrenOptions : parentChildrenOptions}
                                                        />
                                                    </div>
                                                </div>
                                            }

                                            {children && (selected?.title == 'Married' || selected?.title == 'Parent') &&
                                                <div className="col-span-6 sm:col-span-3">
                                                    <label htmlFor="comparisonYear" className="block text-sm font-medium text-gray-700">
                                                        Preview salary for
                                                    </label>
                                                    <div className="mt-1 relative rounded-md shadow-sm w-full">
                                                        <Select
                                                            components={{
                                                                IndicatorSeparator: () => null
                                                            }}
                                                            className="w-full text-sm"
                                                            defaultValue={comparisonYears[0]}
                                                            id="comparisonYear"
                                                            name="comparisonYear"
                                                            value={comparisonYears.find(y => y.value == comparisonYear) || null}
                                                            onChange={(option) => setComparisonYear(option.value)}
                                                            styles={{
                                                                menu: (base) => ({
                                                                    ...base,
                                                                    width: "max-content",
                                                                    minWidth: "100%"
                                                                }),
                                                            }}
                                                            options={comparisonYears}
                                                        />
                                                    </div>
                                                </div>
                                            }

                                            {/* Start Social Security Options */}
                                            <div>
                                                <fieldset>
                                                    <div className="space-y-4">
                                                        <div className="flex items-start">
                                                            <div className="flex items-center h-5">
                                                                <input
                                                                    id="apprentice"
                                                                    name="apprentice"
                                                                    type="checkbox"
                                                                    onChange={(e) => setIsApprentice(e.target.checked)}
                                                                    className={`focus:z-10 focus:ring-transparent h-4 w-4 border-gray-300 rounded${primaryColor ? "" : " text-orange-500"}`}
                                                                />
                                                            </div>
                                                            <div className="ml-3 text-sm">
                                                                <label htmlFor="apprentice" className="font-medium text-gray-700">
                                                                    Are you an apprentice?
                                                                </label>
                                                                <p className="text-gray-500">Apprentices pay less SSC</p>
                                                            </div>
                                                            <QuestionMarkCircleIcon
                                                                className="w-5 h-5 text-gray-500"
                                                                data-tip="&quot;apprentice&quot; means a person over the age of fifteen years who is<br />
                                    bound by a written agreement to serve an employer for a<br />
                                    determined period with a view to acquiring knowledge, including<br />
                                    theory and practice, of a calling in which the employer is<br />
                                    reciprocally bound to instruct that person"/>
                                                            <ReactTooltip effect="solid" multiline={true} />
                                                        </div>
                                                        <div className="flex items-start">
                                                            <div className="flex items-center h-5">
                                                                <input
                                                                    id="age"
                                                                    name="age"
                                                                    type="checkbox"
                                                                    checked={age}
                                                                    onChange={(e) => {
                                                                        setAge(e.target.checked)
                                                                        oldAge && setOldAge(false)
                                                                    }}
                                                                    className={`focus:z-10 focus:ring-transparent h-4 w-4 border-gray-300 rounded${primaryColor ? "" : " text-orange-500"}`}
                                                                />
                                                            </div>
                                                            <div className="ml-3 text-sm">
                                                                <label htmlFor="age" className="font-medium text-gray-700">
                                                                    Are you 17 years or younger?
                                                                </label>
                                                                <p className="text-gray-500">Minors pay less SSC</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-start">
                                                            <div className="flex items-center h-5">
                                                                <input
                                                                    id="oldAge"
                                                                    name="oldAge"
                                                                    type="checkbox"
                                                                    checked={oldAge}
                                                                    onChange={(e) => {
                                                                        setOldAge(e.target.checked)
                                                                        age && setAge(false)
                                                                    }}
                                                                    className={`focus:z-10 focus:ring-transparent h-4 w-4 border-gray-300 rounded${primaryColor ? "" : " text-orange-500"}`}
                                                                />
                                                            </div>
                                                            <div className="ml-3 text-sm">
                                                                <label htmlFor="oldAge" className="font-medium text-gray-700">
                                                                    Were you born before 1st Jan 1962?
                                                                </label>
                                                                <p className="text-gray-500">People born before 1st Jan 1962 pay less SSC</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </fieldset>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className="text-left">
                            <p className={`text-xs mt-2${poweredByTextColor ? "" : " text-gray-200"}`} style={poweredByTextColor && { color: poweredByTextColor }}>Powered by <a className="hover:underline" target="_blank" href="https://www.buddy.hr">Buddy - Cloud HR & Payroll Software.</a></p>
                            <p className={`text-xs mt-2${footerTextColor ? "" : " text-gray-400"}`} style={footerTextColor && { color: footerTextColor }}>The results presented by this payroll calculator are generated through the same system Buddy uses to calculate real payroll results.</p>
                        </div>
                    </div>
                </div>

                <div className="lg:mt-0 sm:col-span-7 ml-1 mr-0 md:mx-4">
                    <div className={`space-y-4 p-6 pb-0 rounded-md${backgroundColor ? "" : " bg-gray-100"}`} style={backgroundColor && { backgroundColor: backgroundColor }}>

                        <div className="grid grid-cols-2 gap-4">

                            <h2 style={{ lineHeight: '36px' }}>Salary Results</h2>
                            <div className="inset-y-0 right-1 flex items-center z-20" style={{ paddingRight: "0.05rem" }}>
                                <Select
                                    styles={{ control: base => ({ ...base, border: 0, boxShadow: "none" }) }}
                                    components={{
                                        IndicatorSeparator: () => null
                                    }}
                                    className="w-full text-sm"
                                    defaultValue={payPeriods[1]}
                                    name="payPeriod"
                                    selected={payPeriods.find(f => f.value == frequency)}
                                    onChange={(val) => {
                                        setPayPeriod(val.value)
                                    }}
                                    options={payPeriods}
                                />
                            </div>
                        </div>
                        <div className="divide-y-2 divide-gray-300">
                            <div className="grid grid-cols-2 gap-4 pb-6 xl:grid-cols-4">
                                <div className="col-span-2">
                                    <div className="flex flex-col">
                                        <div className="-my-2 sm:-mx-2 lg:-mx-8">
                                            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                <div className="shadow overflow-hidden border-b border-gray-200 rounded-lg">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th />
                                                                <th scope="col" className="pl-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Earnings
                                                                </th>
                                                                <th scope="col" className="pr-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Amount
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {
                                                                Object.keys(earnings).map(key => {
                                                                    if (earnings[key].value > 0)
                                                                        return (
                                                                            <tr key={key}>
                                                                                <td className="pl-4 py-2">
                                                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: innerChartColours ? innerChartColours[key] : earningColors[key] }} />
                                                                                </td>
                                                                                <td className="px-0 py-2">
                                                                                    <div className="flex items-center">
                                                                                        <div className="ml-2">
                                                                                            <div className="text-sm text-gray-900">
                                                                                                {earnings[key].name}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="px-2 py-2">
                                                                                    <div className="text-right pr-4 text-sm text-gray-900">€{((Math.round((earnings[key].value + Number.EPSILON) * 100) / 100)).toFixed(2)}</div>
                                                                                </td>
                                                                            </tr>)
                                                                })}
                                                            {payPeriod != 'Year' &&
                                                                <React.Fragment>
                                                                    <tr>
                                                                        <td className="pl-4 py-1">
                                                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'transparent' }} />
                                                                        </td>
                                                                        <td className="px-0 py-1">
                                                                            <div className="flex items-center">
                                                                                <div className="ml-2">
                                                                                    <div className="text-xs text-gray-400">
                                                                                        Weekly Allowance<sup>1</sup>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-1">
                                                                            <div className="text-right text-xs text-gray-400">€121.16</div>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td className="pl-4 py-1">
                                                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'transparent' }} />
                                                                        </td>
                                                                        <td className="px-0 py-1">
                                                                            <div className="flex items-center">
                                                                                <div className="ml-2">
                                                                                    <div className="text-xs text-gray-400">
                                                                                        Statutory Bonus<sup>1</sup>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-6 py-1">
                                                                            <div className="text-right text-xs text-gray-400">€135.10</div>
                                                                        </td>
                                                                    </tr>
                                                                </React.Fragment>
                                                            }
                                                        </tbody>
                                                        <tfoot className="bg-white">
                                                            <tr>
                                                                <th />
                                                                <th scope="col" className="pl-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                    Total
                                                                </th>
                                                                <th scope="col" className="text-right pr-6 text-xs text-gray-700">
                                                                    €{((Math.round((earningsTotal + Number.EPSILON) * 100) / 100)).toFixed(2)}
                                                                </th>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-span-2">
                                    <div className="flex flex-col">
                                        <div className="-my-2 sm:-mx-2 lg:-mx-8">
                                            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                                <div className="shadow overflow-hidden border-b border-gray-200 rounded-lg">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th />
                                                                <th scope="col" className="pl-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Deductions
                                                                </th>
                                                                <th scope="col" className="pr-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                    Amount
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {
                                                                Object.keys(deductions).map(key => {
                                                                    if ((deductions[key].payer == "Employee" || deductions[key].payer == "Both") && deductions[key].value > 0)
                                                                        return (
                                                                            <tr key={key}>
                                                                                <td className="pl-4 py-2">
                                                                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: outerChartColours ? outerChartColours[key] : deductionColors[key] }} />
                                                                                </td>
                                                                                <td className="px-0 py-2">
                                                                                    <div className="flex items-center">
                                                                                        <div className="ml-2">
                                                                                            <div className="text-sm text-gray-900">
                                                                                                {deductions[key].name}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </td>
                                                                                <td className="pr-2 py-2">
                                                                                    <div className="text-right pr-4 text-sm text-gray-900">€{((Math.round((deductions[key].value + Number.EPSILON) * 100) / 100)).toFixed(2)}</div>
                                                                                </td>
                                                                            </tr>)
                                                                })}
                                                        </tbody>
                                                        <tfoot className="bg-white">
                                                            <tr>
                                                                <th />
                                                                <th scope="col" className="pl-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                                                    Total
                                                                </th>
                                                                <th scope="col" className="text-right pr-6 text-xs text-gray-700">
                                                                    €{((Math.round((deductionsTotal + Number.EPSILON) * 100) / 100)).toFixed(2)}
                                                                </th>
                                                            </tr>
                                                        </tfoot>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-2 text-xl font-bold text-gray-700 uppercase flex text-left relative">
                                <div className="flex-1">Take-home pay</div>
                                <div>€{net && net.toFixed(2)}</div>
                                {colaDifference && year != 2023 ?
                                    <div className="text-lg" style={{ position: 'absolute', top: '3rem', right: '0.5rem' }}>€{colaDifference.toFixed(2)} <span style={{ color: '#0e9f6e' }} >↑</span></div> : null
                                }
                            </div>
                            {(net &&
                                <div>
                                    <div className="grid 2xl:grid-cols-2 p-2 text-left">
                                        <div style={{
                                            marginLeft: -20
                                        }}>
                                            <HighchartsReact
                                                highcharts={Highcharts}
                                                options={chartOptions}
                                            />
                                        </div>
                                        {!showDownloadForm &&
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-700 uppercase">Detailed Salary Report</h3>
                                                <ul className="text-sm mt-2 mb-2 font-normal" style={{
                                                    listStyle: 'disc',
                                                    marginLeft: 40,
                                                }}>
                                                    <li>Annualised Payroll Calculations</li>
                                                    <li>Employer Social Security Contribution</li>
                                                    <li>Maternity Fund Contribution</li>
                                                    <li>Value of Vacation and Sick Leave</li>
                                                    <li>Cost to Employer</li>
                                                    <li>Cost of Productive Hours</li>
                                                    <li>Definitions of Payroll Terminology</li>
                                                </ul>
                                                <button
                                                    className={`mt-2 py-2 text-base border border-transparent leading-5 font-medium rounded-md text-white transition duration-150 ease-in-out w-full${primaryColor ? "" : " bg-orange-400 hover:bg-orange-500"}`}
                                                    style={primaryColor && { backgroundColor: primaryColor }}
                                                    onClick={() => handleClick()}
                                                >
                                                    Request Your Copy
                                                </button>
                                            </div>
                                        }
                                        {showDownloadForm &&
                                            <div className="grid 2xl:grid-rows-6 gap-2 items-center">


                                                {error &&
                                                    <div className="rounded-md bg-red-50 p-4 mb-6">
                                                        <div className="flex">
                                                            <div className="ml-3">
                                                                <h3 className="text-sm leading-5 font-medium text-red-800">
                                                                    Error: {error}
                                                                </h3>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }

                                                <div className="grid grid-cols-2 gap-2 items-center">
                                                    <div>
                                                        <label htmlFor="name" className="sr-only">
                                                            Name
                                                        </label>
                                                        <div className="rounded-md shadow-sm">
                                                            <input value={data.name} onChange={handleChange} name="name" id="name" placeholder="Name" required className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5" />
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <label htmlFor="surname" className="sr-only">
                                                            Surname
                                                        </label>
                                                        <div className="rounded-md shadow-sm">
                                                            <input value={data.surname} onChange={handleChange} name="surname" id="surname" placeholder="Surname" required className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="items-center">
                                                    <label htmlFor="organisation" className="sr-only">
                                                        Organisation
                                                    </label>
                                                    <div className="rounded-md shadow-sm">
                                                        <input value={data.organisation} onChange={handleChange} name="organisation" id="organisation" placeholder="Organisation" required className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5" />
                                                    </div>
                                                </div>

                                                <div className="items-center">
                                                    <label htmlFor="noOfEmployees" className="sr-only">
                                                        No of Employees
                                                    </label>
                                                    <div className="rounded-md shadow-sm">
                                                        <select onChange={handleChange} name="noOfEmployees" id="noOfEmployees" className="form-select block w-full pl-3 pr-10 py-2 text-base leading-6 border-gray-300 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 sm:text-sm sm:leading-5">
                                                            <option value="1-5">1-5 Employees</option>
                                                            <option value="6-10">6-10 Employees</option>
                                                            <option value="11-20">11-20 Employees</option>
                                                            <option value="21-40">21-40 Employees</option>
                                                            <option value="41-80">41-80 Employees</option>
                                                            <option value="81-200">81-200 Employees</option>
                                                            <option value="200+">200+ Employees</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label htmlFor="email" className="sr-only">
                                                        Email
                                                    </label>
                                                    <div className="rounded-md shadow-sm">
                                                        <input value={data.email} onChange={handleChange} name="email" id="email" placeholder="Email" required className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5" />
                                                    </div>
                                                </div>

                                                <span className="block w-full h-full rounded-md shadow-sm text-center">

                                                    {
                                                        saving &&
                                                        <svg className="text-orange-600 inline-block animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    }
                                                    {!saving && !downloadLink &&
                                                        <button onClick={() => submitForm()} className={`py-2 text-base w-full h-full flex justify-center items-center px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none transition duration-150 ease-in-out${primaryColor ? "" : " bg-orange-400 hover:bg-orange-500"}`} style={primaryColor && { backgroundColor: primaryColor }}>
                                                            Generate my Report
                                                        </button>
                                                    }
                                                    {downloadLink &&
                                                        <a href={downloadLink} className={`py-2 text-base w-full h-full flex justify-center items-center px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none transition duration-150 ease-in-out${primaryColor ? "" : " bg-orange-400 hover:bg-orange-500"}`} style={primaryColor && { backgroundColor: primaryColor }}>
                                                            Download Report
                                                        </a>
                                                    }
                                                </span>

                                            </div>
                                        }
                                    </div>
                                    <ol className="list-decimal pb-4 text-left">
                                        <li className="py-1 block text-xs text-gray-600">1. Weekly Allowance is Paid in March and September, Statutory Bonus is paid every June and December</li>
                                        <li className="py-1 block text-xs text-gray-600 ">2. Presented results are for a Full-time role based on the first payroll period. SSC contributions may vary based on the number of weeks in a Month.</li>
                                    </ol>
                                </div>) || null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


const mapStateToProps = (state) => {
    const earnings = state?.salaries?.earnings || {}
    const deductions = state?.salaries?.deductions || {}
    const net = state?.salaries?.net
    const colaDifference = state?.salaries?.cola?.net ? (parseFloat(net) || 0) - (parseFloat(state?.salaries?.cola?.net) || 0) : 0
    const salaryCalculator = state?.salaryCalculator
    const profile = state?.my?.profile || localStorage.getItem('userData') && JSON.parse(localStorage.getItem('userData'))
    return {
        saving: salaryCalculator?.creating,
        downloadLink: salaryCalculator?.downloadLink,
        profile,
        earnings,
        deductions,
        net,
        colaDifference,
    }
}

const mapDispatchToProps = dispatch => ({
    calculateSalary: (data) => dispatch(SALARYCALCULATORACTIONS.calculateSalary(data)),
    requestReport: (data) => dispatch(SALARYCALCULATORACTIONS.requestReport(data)),
    clearReportLink: () => dispatch(SALARYCALCULATORACTIONS.clearReportLink({name: "generateSamplePayslip"})),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(SalaryCalculator)
